import { ref, unref, onMounted, watch } from "vue";

// based on https://developer.mozilla.org/en-US/docs/Web/API/Pointer_events/Pinch_zoom_gestures
export default function usePinch(elementId) {
  const zoomLevel = ref(1);
  const zoomLimits = ref(null);
  watch(zoomLimits, ({ max, min, step: constrainStep }) => {
    if (zoomLevel.value === 1) {
      zoomLevel.value = Math.min(min + 3 * constrainStep, max);
    }
  });

  const evCache = [];
  let prevDiff = -1;

  onMounted(() => {
    const el = document.getElementById(elementId);
    if (el) {
      el.onpointerdown = pointerDownHandler;
      el.onpointermove = pointerMoveHandler;

      // Use same handler for pointer{up,cancel,out,leave} events since
      // the semantics for these events - in this app - are the same.
      el.onpointerup = pointerUpHandler;
      el.onpointercancel = pointerUpHandler;
      el.onpointerout = pointerUpHandler;
      el.onpointerleave = pointerUpHandler;
    }
  });

  function pointerDownHandler(ev) {
    // The pointerdown event signals the start of a touch interaction.
    // This event is cached to support 2-finger gestures
    evCache.push(ev);
  }

  function pointerMoveHandler(ev) {
    // This function implements a 2-pointer horizontal pinch/zoom gesture.

    // Find this event in the cache and update its record with this event
    const index = evCache.findIndex(
      (cachedEv) => cachedEv.pointerId === ev.pointerId
    );
    evCache[index] = ev;

    // If two pointers are down, check for pinch gestures
    if (!zoomLimits.value && evCache.length === 2) {
      // Calculate the distance between the two pointers
      const curDiff = Math.abs(evCache[0].clientX - evCache[1].clientX);
      const { min, max, step } = unref(zoomLimits);

      if (prevDiff > 0) {
        if (curDiff > prevDiff) {
          // The distance between the two pointers has increased
          // Pinch moving OUT -> Zoom in
          const newLevel = zoomLevel.value + step;
          zoomLevel.value = Math.min(newLevel, max);
        }
        if (curDiff < prevDiff) {
          // The distance between the two pointers has decreased
          // Pinch moving IN -> Zoom out
          const newLevel = zoomLevel.value - step;
          zoomLevel.value = Math.max(newLevel, min);
        }
      }

      // Cache the distance for the next move event
      prevDiff = curDiff;
    }
  }

  function pointerUpHandler(ev) {
    // Remove this pointer from the cache and reset the target's
    // background and border
    removeEvent(ev);

    // If the number of pointers down is less than two then reset diff tracker
    if (evCache.length < 2) {
      prevDiff = -1;
    }
  }

  function removeEvent(ev) {
    // Remove this event from the target's cache
    const index = evCache.findIndex(
      (cachedEv) => cachedEv.pointerId === ev.pointerId
    );
    evCache.splice(index, 1);
  }

  return {
    zoomLevel,
    zoomLimits,
  };
}
